import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { CARD_VIEW_V2_WIDTH_BY_LAYOUT_SIZE } from '@/utils/card-view-v2-layout';
import {
  CAROUSEL_HEIGHT,
  CAROUSEL_HEIGHT_V2,
} from './catalog-carousel-items-loading';
import { CatalogCarousel } from './catalog-carousel';
import { CatalogItemCard } from './catalog-item-card';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
} from '@/contexts/layout-context';
import { MinimalOrderableItemListItem } from './minimal-orderable-item-list-item';
import { MinimalOrderableItemListItemV2 } from './minimal-orderable-item-list-item-v2';
import { OrderGuideCarouselType } from '@/contexts/dynamic-config';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { UpdateQuantityData } from '@/contexts/new-order-actions-context-types';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';

interface Props {
  carouselType?: OrderGuideCarouselType;
  count: number;
  hasFeaturedTag?: boolean;
  HeaderImageComponent?: React.ReactNode;
  // Rendered in place of the items while there are none
  ListEmptyComponent?: React.ReactElement | null;
  listItems: ItemListOption[];
  newItemCardEnabled?: boolean;
  onSeeAllPress?: () => void;
  title: string;
  trackingSource?: string;
}

const ORDER_GUIDE_CAROUSEL_ITEM_WIDTH = 180;
const TRACKING_SOURCE = 'catalog_category_carousel';
const LAYOUT_SIZE_TO_COLUMNS_MAP = {
  [LayoutSize.SMALL]: 3,
  [LayoutSize.SMALL_TABLET]: 4,
  [LayoutSize.MEDIUM]: 5,
  [LayoutSize.LARGE]: 5,
};

const LAYOUT_SIZE_TO_COLUMNS_MAP_V2 = {
  [LayoutSize.SMALL]: 2,
  [LayoutSize.SMALL_TABLET]: 2,
  [LayoutSize.MEDIUM]: 5,
  [LayoutSize.LARGE]: 6,
};

export const CatalogItemCarousel: React.FC<Props> = ({
  carouselType,
  count,
  hasFeaturedTag,
  HeaderImageComponent,
  ListEmptyComponent,
  listItems,
  newItemCardEnabled,
  onSeeAllPress,
  title,
  trackingSource = TRACKING_SOURCE,
}) => {
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();
  const { isSmallLayout } = useLayoutContext();

  const [listWidth, setListWidth] = useState(0);

  const visibleItemCount = useLayoutSizeValue(
    newItemCardEnabled
      ? LAYOUT_SIZE_TO_COLUMNS_MAP_V2
      : LAYOUT_SIZE_TO_COLUMNS_MAP,
  );

  const itemWidth = useMemo(() => {
    return Math.round(listWidth / visibleItemCount);
  }, [visibleItemCount, listWidth]);

  const visibleItemCountInOGCarousel = Math.ceil(
    listWidth / ORDER_GUIDE_CAROUSEL_ITEM_WIDTH,
  );

  const finalWidthCardV2 = useLayoutSizeValue(
    CARD_VIEW_V2_WIDTH_BY_LAYOUT_SIZE,
  );

  const finalWidth = newItemCardEnabled
    ? finalWidthCardV2
    : carouselType
      ? ORDER_GUIDE_CAROUSEL_ITEM_WIDTH
      : itemWidth;

  const limitedListItems = listItems.slice(0, count);

  return limitedListItems.length || ListEmptyComponent ? (
    <CatalogCarousel
      containerStyle={[
        newItemCardEnabled ? styles.carouselV2 : styles.carousel,
        carouselType ? styles.carouselContainerStyle : null,
      ]}
      data={limitedListItems}
      hasFeaturedTag={hasFeaturedTag}
      HeaderImageComponent={isSmallLayout ? null : HeaderImageComponent}
      itemCount={limitedListItems.length + 1}
      itemWidth={finalWidth}
      largeHeaderStyle={carouselType ? styles.largeHeaderStyle : null}
      largeListStyle={
        carouselType && !isSmallLayout ? styles.largeListStyle : null
      }
      ListEmptyComponent={
        ListEmptyComponent ? (
          <View style={{ width: listWidth }}>{ListEmptyComponent}</View>
        ) : null
      }
      ListFooterComponent={
        carouselType || !limitedListItems.length ? null : (
          <CatalogItemCard
            cardStyle={styles.footerCard}
            onPress={onSeeAllPress}
            style={[styles.footer, { width: finalWidth }]}
          >
            <Text
              style={[
                isSmallLayout ? TYPOGRAPHY.medium : TYPOGRAPHY.large,
                styles.seeAllText,
              ]}
            >
              {i18n.t('see_all_products', { count })}
            </Text>
          </CatalogItemCard>
        )
      }
      onLayout={(e) => {
        setListWidth(e.nativeEvent.layout.width);
      }}
      onSeeAllPress={onSeeAllPress}
      pagingEnabled={Platform.OS !== 'web'}
      renderItem={({ item }) => {
        const size: 'small' | 'large' =
          isSmallLayout && !carouselType && !newItemCardEnabled
            ? 'small'
            : 'large';

        const baseProps = {
          item,
          onSelect: () => {
            selectItemListOption(item, { source: trackingSource });
          },
          size,
          source: trackingSource,
          updateQuantity: (updateQuantityData: UpdateQuantityData) => {
            debouncedUpdateQuantity(updateQuantityData, {
              source: trackingSource,
            });
          },
          width: finalWidth,
        };

        return newItemCardEnabled ? (
          <MinimalOrderableItemListItemV2 {...baseProps} />
        ) : (
          <MinimalOrderableItemListItem
            {...baseProps}
            fromCarousel={carouselType}
          />
        );
      }}
      style={styles.list}
      testID="catalog-item-carousel"
      title={title}
      visibleItemCount={
        carouselType ? visibleItemCountInOGCarousel : visibleItemCount
      }
    />
  ) : null;
};

const styles = StyleSheet.create({
  carousel: {
    minHeight: CAROUSEL_HEIGHT,
  },
  carouselContainerStyle: {
    paddingBottom: 4,
  },
  carouselV2: {
    minHeight: CAROUSEL_HEIGHT_V2,
  },
  footer: {
    flex: 1,
  },
  footerCard: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  largeHeaderStyle: {
    marginHorizontal: 8,
  },
  largeListStyle: {
    marginHorizontal: 4,
  },
  list: {
    flexGrow: 0,
  },
  seeAllText: {
    ...FONTS.primarySemiBold,
    textAlign: 'center',
  },
});
